/**
 * Ocorrências — o histórico de alertas, por PROBLEMA.
 *
 * Até 11/09/26 o histórico só existia em /admin/alerts, atrás de
 * `role="admin"`. O operador que recebe o push às 3h não tinha onde conferir
 * o que aconteceu: o alerta vibrava no bolso e sumia. Esta tela existe para
 * esse momento.
 *
 * A mudança de agora é o que cada LINHA significa. Antes era um evento; um
 * único problema — a câmara subiu às 3h e normalizou às 4h — aparecia como
 * seis linhas (disparou, lembrete, notificou por e-mail, notificou por
 * WhatsApp, reconhecido, recuperou), e quem lia contava seis ocorrências onde
 * houve uma. Agora a linha é o episódio e os eventos ficam no detalhe.
 *
 * Duas decisões que o uso real ditou:
 *
 *  • ESCOPO PRIMEIRO. O autor deste sistema confundiu um alerta de site com
 *    um de unidade porque os dois nomes apareciam na mesma coluna, sem dizer
 *    o que eram. Se enganou quem construiu, o cliente não tem chance — então
 *    o escopo é a primeira coisa que a linha diz.
 *  • SEM AÇÕES NA LISTA. Reconhecer e comentar vivem no detalhe, onde a
 *    pessoa já viu o que aconteceu. Botão de ack numa linha de tabela é
 *    convite a silenciar um alerta sem ler.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, FilterX, MessageSquare, Phone } from "lucide-react";

import { Badge } from "@/components/Badge";
import { Card, CardBody } from "@/components/Card";
import { DataTable, type Column } from "@/components/DataTable";
import { EpisodioModal } from "@/components/EpisodioModal";
import { Button } from "@/components/Button";
import { Field, Input, Select } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useEpisodios, useTenants } from "@/hooks/queries";
import { RULE_KIND_LABELS } from "@/lib/ruleKinds";
import { formatDate, timeAgo } from "@/lib/format";
import type { Episodio, EpisodioStatus, RuleKind, Severity } from "@/types";
import { SEVERIDADE_LABEL, SEVERIDADE_OPCOES, SEVERIDADE_TOM } from "@/lib/severidade";





const STATUS_LABEL: Record<EpisodioStatus, string> = {
  aberto: "Em aberto",
  reconhecido: "Reconhecido",
  resolvido: "Resolvido",
};

const STATUS_TOM: Record<EpisodioStatus, "danger" | "warning" | "success"> = {
  aberto: "danger",
  reconhecido: "warning",
  resolvido: "success",
};

export function OcorrenciasPage() {
  const auth = useAuth();
  const [clienteEscolhido, setClienteEscolhido] = useState("");
  const [situacao, setSituacao] = useState<"" | EpisodioStatus>("");
  const [severidade, setSeveridade] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [pagina, setPagina] = useState(1);
  const [abertoId, setAbertoId] = useState<string | null>(null);

  // Deep link: `?episodio=<id>` abre o detalhe direto.
  //
  // É o que faz o sino levar a pessoa AO alerta que ela acabou de ver, em vez
  // de largá-la numa lista pra procurar. O parâmetro sai da URL assim que o
  // modal abre, senão voltar pela lista reabriria o mesmo modal pra sempre.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const pedido = params.get("episodio");
    if (!pedido) return;
    setAbertoId(pedido);
    const limpo = new URLSearchParams(params);
    limpo.delete("episodio");
    setParams(limpo, { replace: true });
  }, [params, setParams]);

  const tenants = useTenants(auth.isSuperAdmin);

  // Só o super_admin manda tenant_id. Para os demais o parâmetro nem existe:
  // o backend força o tenant do JWT, e mandar algo daqui só criaria a ilusão
  // de que a tela escolhe o escopo.
  const tenantId = auth.isSuperAdmin ? clienteEscolhido || undefined : undefined;

  const POR_PAGINA = 50;

  // Todo filtro vai pro servidor, inclusive a severidade.
  //
  // Enquanto a lista cabia numa resposta só, filtrar no cliente dava o mesmo
  // resultado. Com paginação, dá outro: filtraria só a página carregada, e a
  // pessoa veria "3 críticas" numa página e nenhuma na seguinte sem que nada
  // tivesse mudado.
  //
  // A data vai como início e fim do DIA escolhido — quem digita 17/09 quer o
  // dia inteiro, não a meia-noite em ponto.
  const episodios = useEpisodios({
    tenant_id: tenantId,
    status: situacao || undefined,
    severity: (severidade || undefined) as Severity | undefined,
    de: de ? `${de}T00:00:00` : undefined,
    ate: ate ? `${ate}T23:59:59` : undefined,
    pagina,
    por_pagina: POR_PAGINA,
  });

  // Envelope, mas defensivo: ver o comentário do DataTable sobre a janela
  // em que o bundle antigo ainda está ativo.
  const linhas = Array.isArray(episodios.data?.items) ? episodios.data.items : [];
  const total = episodios.data?.total ?? 0;
  const ultimaPagina = Math.max(1, Math.ceil(total / POR_PAGINA));

  // Trocar de filtro com a página 7 aberta devolveria uma lista vazia que
  // parece "nada encontrado" — quando na verdade o resultado tem 2 páginas.
  const temFiltro = !!(clienteEscolhido || situacao || severidade || de || ate);

  function limpar() {
    setClienteEscolhido("");
    setSituacao("");
    setSeveridade("");
    setDe("");
    setAte("");
    setPagina(1);
  }

  function filtrar<T>(set: (v: T) => void) {
    return (v: T) => {
      setPagina(1);
      set(v);
    };
  }

  const columns: Column<Episodio>[] = [
    ...(auth.isSuperAdmin
      ? [{
          key: "tenant",
          header: "Cliente",
          cell: (r: Episodio) => (
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {r.tenant_name ?? r.tenant_id.slice(0, 8)}
            </span>
          ),
        } as Column<Episodio>]
      : []),
    {
      key: "quando",
      header: "Quando",
      cell: (r) => (
        <div>
          <div className="text-sm">{timeAgo(r.inicio)}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(r.inicio)}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Situação",
      cell: (r) => (
        <div className="flex flex-col gap-1">
          <Badge tone={STATUS_TOM[r.status]}>{STATUS_LABEL[r.status]}</Badge>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {r.duracao_min != null ? `durou ${r.duracao_min} min` : "em andamento"}
          </span>
        </div>
      ),
    },
    {
      key: "severity",
      header: "Sev",
      hideOnMobile: true,
      cell: (r) =>
        r.severity ? (
          <Badge tone={SEVERIDADE_TOM[r.severity]}>{SEVERIDADE_LABEL[r.severity]}</Badge>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
        ),
    },
    {
      key: "onde",
      header: "Onde",
      cell: (r) =>
        r.escopo === "site" ? (
          <span className="inline-flex items-center gap-1.5">
            <Badge tone="neutral">site</Badge>
            <span className="text-slate-700 dark:text-slate-300">
              {r.site_name ?? "não identificado"}
            </span>
          </span>
        ) : (
          <div>
            <div className="text-slate-700 dark:text-slate-300">
              {r.equipment_name ?? r.equipment_id?.slice(0, 8) ?? "—"}
            </div>
            {r.site_name && (
              <div className="text-xs text-slate-500 dark:text-slate-400">{r.site_name}</div>
            )}
          </div>
        ),
    },
    {
      key: "problema",
      header: "Problema",
      cell: (r) => (
        <div>
          <div className="text-sm text-slate-800 dark:text-slate-200">
            {r.rule_kind
              ? RULE_KIND_LABELS[r.rule_kind as RuleKind] ?? r.rule_kind
              : "—"}
          </div>
          {r.mensagem && (
            <div className="line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
              {r.mensagem}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "sinais",
      header: "",
      align: "right",
      cell: (r) => (
        <span className="inline-flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          {r.teve_chamada && (
            <span className="inline-flex items-center gap-1" title="Houve ligação telefônica">
              <Phone className="h-3.5 w-3.5" />
            </span>
          )}
          {r.n_comentarios > 0 && (
            <span className="inline-flex items-center gap-1" title="Tem registro do operador">
              <MessageSquare className="h-3.5 w-3.5" />
              {r.n_comentarios}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Ocorrências"
        description={
          auth.isSuperAdmin
            ? "Cada linha é um problema, do disparo até normalizar. Sem cliente selecionado, mostra todos."
            : "Cada linha é um problema, do disparo até normalizar. Clique para ver o que aconteceu."
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {auth.isSuperAdmin && (
          <Field label="Cliente">
            <Select
              value={clienteEscolhido}
              onChange={(e) => filtrar(setClienteEscolhido)(e.target.value)}
              options={[
                { value: "", label: "Todos os clientes" },
                ...(tenants.data ?? []).map((t) => ({
                  value: t.id,
                  label: `${t.name} (${t.slug})`,
                })),
              ]}
            />
          </Field>
        )}
        <Field label="Situação">
          <Select
            value={situacao}
            onChange={(e) =>
              filtrar(setSituacao)(e.target.value as "" | EpisodioStatus)}
            options={[
              { value: "", label: "Todas" },
              { value: "aberto", label: "Em aberto" },
              { value: "reconhecido", label: "Reconhecidas" },
              { value: "resolvido", label: "Resolvidas" },
            ]}
          />
        </Field>
        <Field label="Severidade">
          <Select
            value={severidade}
            onChange={(e) => filtrar(setSeveridade)(e.target.value)}
            options={[{ value: "", label: "Todas" }, ...SEVERIDADE_OPCOES]}
          />
        </Field>
        {/* Data do COMEÇO do problema — é o que a pessoa tem em mente ao
            escolher um período, e não "teve algum evento nele". */}
        <Field label="De">
          <Input
            type="date"
            value={de}
            max={ate || undefined}
            onChange={(e) => filtrar(setDe)(e.target.value)}
          />
        </Field>
        <Field label="Até">
          <Input
            type="date"
            value={ate}
            min={de || undefined}
            onChange={(e) => filtrar(setAte)(e.target.value)}
          />
        </Field>
        {/* Só aparece quando há o que limpar: botão permanentemente inerte
            vira ruído, e some o "o que esse botão faz?" quando ele não tem
            efeito nenhum. */}
        {temFiltro && (
          <div className="flex items-end">
            <Button
              variant="ghost"
              size="sm"
              icon={<FilterX className="h-4 w-4" />}
              onClick={limpar}
            >
              Limpar filtros
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardBody className="p-0">
          <DataTable
            data={linhas}
            columns={columns}
            loading={episodios.isLoading}
            rowKey={(r) => r.episodio_id}
            onRowClick={(r) => setAbertoId(r.episodio_id)}
            emptyTitle="Nenhuma ocorrência"
            emptyDescription={
              situacao || severidade || de || ate
                ? "Nada neste filtro. Tente ampliar o período ou limpar a situação."
                : "Nenhum alerta registrado ainda. É a notícia boa."
            }
          />
        </CardBody>
      </Card>

      {/* Paginação. O total vem do servidor junto com a página: sem ele não
          dá pra dizer onde a pessoa está nem desabilitar o "próxima", e aí
          ela clica até dar vazio pra descobrir que acabou. */}
      {total > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {(pagina - 1) * POR_PAGINA + 1}–
            {Math.min(pagina * POR_PAGINA, total)} de {total}
            {total === 1 ? " ocorrência" : " ocorrências"}
          </span>
          {ultimaPagina > 1 && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<ChevronLeft className="h-4 w-4" />}
                disabled={pagina <= 1 || episodios.isFetching}
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {pagina} / {ultimaPagina}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={pagina >= ultimaPagina || episodios.isFetching}
                onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
              >
                Próxima
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <EpisodioModal episodioId={abertoId} onClose={() => setAbertoId(null)} />
    </div>
  );
}
